import { useState, useEffect } from 'react';
import { Search, X } from 'lucide-react';
import type { InventoryFilters as FiltersType, InventoryFilterOptions } from '../../types/inventory';

interface InventoryFiltersProps {
    filters: FiltersType;
    filterOptions: InventoryFilterOptions | null;
    onFilterChange: (filters: FiltersType) => void;
}

export function InventoryFilters({ filters, filterOptions, onFilterChange }: InventoryFiltersProps) {
    const [searchInput, setSearchInput] = useState(filters.search || '');

    // Debounce search input
    useEffect(() => {
        const timer = setTimeout(() => {
            if (searchInput !== (filters.search || '')) {
                onFilterChange({ ...filters, search: searchInput || undefined });
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [searchInput]);

    const handleSelectChange = (key: keyof FiltersType, value: string) => {
        const newFilters = { ...filters, [key]: value || undefined };
        // When changing class filter, clear the other class filter
        if (key === 'inventoryClass') {
            delete newFilters.classGroup;
        }
        if (key === 'classGroup') {
            delete newFilters.inventoryClass;
        }
        // When changing parent category, clear the granular category
        if (key === 'parentCategory') {
            delete newFilters.category;
        }
        // A warehouse of another company would leave an empty list behind a blank dropdown
        if (key === 'companyCode' && value && !newFilters.warehouseCode?.startsWith(`${value}|`)) {
            delete newFilters.warehouseCode;
        }
        onFilterChange(newFilters);
    };

    const clearFilters = () => {
        setSearchInput('');
        onFilterChange({ classGroup: 'Piombi' });
    };

    // Filter categories based on selected parent category
    const filteredCategories = filterOptions?.categories.filter(c =>
        !filters.parentCategory || c.parentCode === filters.parentCategory
    ) ?? [];

    const hasActiveFilters = Object.entries(filters).some(([k, v]) =>
        v !== undefined && v !== '' && !(k === 'classGroup' && v === 'Piombi')
    );

    // Determine the current class filter value for the dropdown
    const classFilterValue = filters.inventoryClass
        ? `class-${filters.inventoryClass}`
        : filters.classGroup === 'Piombi'
            ? 'group-Piombi'
            : filters.classGroup === 'all'
                ? ''
                : '';

    const handleClassFilterChange = (value: string) => {
        if (!value) {
            // "All Classes" selected - explicitly request all classes
            onFilterChange({ ...filters, inventoryClass: undefined, classGroup: 'all' });
        } else if (value.startsWith('class-')) {
            onFilterChange({ ...filters, inventoryClass: value.replace('class-', ''), classGroup: undefined });
        } else if (value.startsWith('group-')) {
            onFilterChange({ ...filters, classGroup: value.replace('group-', ''), inventoryClass: undefined });
        }
    };

    const selectClass = "h-9 rounded-md border border-gray-300 px-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500";

    return (
        <div className="space-y-2">
            {/* Row 1: Search + Organisation filters */}
            <div className="flex flex-wrap items-center gap-3">
                <div className="relative">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Search product, code, supplier..."
                        value={searchInput}
                        onChange={e => setSearchInput(e.target.value)}
                        className="h-9 w-64 rounded-md border border-gray-300 pl-9 pr-3 text-sm placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                </div>

                <select
                    value={filters.companyCode || ''}
                    onChange={e => handleSelectChange('companyCode', e.target.value)}
                    className={selectClass}
                >
                    <option value="">All Companies</option>
                    {filterOptions?.companies.map(c => (
                        <option key={c.code} value={c.code}>{c.name} ({c.code})</option>
                    ))}
                </select>

                <select
                    value={filters.businessUnit || ''}
                    onChange={e => handleSelectChange('businessUnit', e.target.value)}
                    className={selectClass}
                >
                    <option value="">All Business Units</option>
                    {filterOptions?.businessUnits.map(bu => (
                        <option key={bu} value={bu}>{bu}</option>
                    ))}
                </select>

                <select
                    value={classFilterValue}
                    onChange={e => handleClassFilterChange(e.target.value)}
                    className={selectClass}
                >
                    <option value="">All Classes</option>
                    <option value="group-Piombi">Piombi (Class 5+6)</option>
                    {filterOptions?.inventoryClasses.map(c => (
                        <option key={c.code} value={`class-${c.code}`}>{c.name}</option>
                    ))}
                </select>

                {hasActiveFilters && (
                    <button
                        onClick={clearFilters}
                        className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100"
                    >
                        <X className="h-3 w-3" />
                        Clear
                    </button>
                )}

                {filters.itemId && (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-orange-100 px-3 py-1 text-xs font-medium text-orange-700">
                        Filtered: {filters.itemId}
                        <button
                            onClick={() => onFilterChange({ ...filters, itemId: undefined })}
                            className="rounded-full p-0.5 hover:bg-orange-200"
                            title="Remove item filter"
                        >
                            <X className="h-3 w-3" />
                        </button>
                    </span>
                )}
            </div>

            {/* Row 2: Product + Location filters */}
            <div className="flex flex-wrap items-center gap-3">
                <select
                    value={filters.parentCategory || ''}
                    onChange={e => handleSelectChange('parentCategory', e.target.value)}
                    className={selectClass}
                >
                    <option value="">All Product Groups</option>
                    {filterOptions?.parentCategories.map(c => (
                        <option key={c.code} value={c.code}>{c.name}</option>
                    ))}
                </select>

                <select
                    value={filters.category || ''}
                    onChange={e => handleSelectChange('category', e.target.value)}
                    className={selectClass}
                >
                    <option value="">All Subcategories</option>
                    {filteredCategories.map(c => (
                        <option key={c.code} value={c.code}>{c.name}</option>
                    ))}
                </select>

                <span className="mx-1 h-5 w-px bg-gray-200" />

                <select
                    value={filters.supplierCode || ''}
                    onChange={e => handleSelectChange('supplierCode', e.target.value)}
                    className={selectClass}
                >
                    <option value="">All Suppliers</option>
                    {filterOptions?.suppliers.map(s => (
                        <option key={s.code} value={s.code}>{s.name}</option>
                    ))}
                </select>

                <select
                    value={filters.warehouseCountry || ''}
                    onChange={e => handleSelectChange('warehouseCountry', e.target.value)}
                    className={selectClass}
                >
                    <option value="">All Countries</option>
                    {filterOptions?.warehouseCountries.map(c => (
                        <option key={c} value={c}>{c}</option>
                    ))}
                </select>

                <select
                    value={filters.warehouseCode || ''}
                    onChange={e => handleSelectChange('warehouseCode', e.target.value)}
                    className={selectClass}
                >
                    <option value="">All Warehouses</option>
                    {filterOptions?.warehouses.map(w => (
                        <option key={w.key} value={w.key}>{w.name} ({w.country || w.companyCode})</option>
                    ))}
                </select>

                <span className="mx-1 h-5 w-px bg-gray-200" />

                {/* Betreuer. Choosing one clears "my customers" - two scope filters at once
                    would silently intersect, and the narrower one would look broken. */}
                <select
                    value={filters.salesManagerId || ''}
                    onChange={e => onFilterChange({
                        ...filters,
                        salesManagerId: e.target.value || undefined,
                        onlyMyCustomers: e.target.value ? undefined : filters.onlyMyCustomers,
                    })}
                    className={selectClass}
                >
                    <option value="">All Sales Managers</option>
                    {filterOptions?.salesManagers?.map(s => (
                        <option key={s.code} value={s.code}>{s.name}</option>
                    ))}
                </select>

                {/* Offered only to users who are sales managers with customers - 37 of the 96
                    people with access to this page are not, and a toggle that can only fail
                    is worse than no toggle. */}
                {filterOptions?.isSalesManager && (
                    <label className="inline-flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
                        <button
                            type="button"
                            role="switch"
                            aria-checked={filters.onlyMyCustomers === '1'}
                            onClick={() => onFilterChange({
                                ...filters,
                                onlyMyCustomers: filters.onlyMyCustomers === '1' ? undefined : '1',
                                salesManagerId: undefined,
                            })}
                            className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${filters.onlyMyCustomers === '1' ? 'bg-blue-600' : 'bg-gray-300'}`}
                        >
                            <span
                                className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition ${filters.onlyMyCustomers === '1' ? 'translate-x-4' : 'translate-x-0'}`}
                            />
                        </button>
                        Only my customers
                    </label>
                )}

                <label className="inline-flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
                    <button
                        type="button"
                        role="switch"
                        aria-checked={filters.onlyNewProducts === '1'}
                        onClick={() => onFilterChange({ ...filters, onlyNewProducts: filters.onlyNewProducts === '1' ? undefined : '1' })}
                        className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${filters.onlyNewProducts === '1' ? 'bg-blue-600' : 'bg-gray-300'}`}
                    >
                        <span
                            className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition ${filters.onlyNewProducts === '1' ? 'translate-x-4' : 'translate-x-0'}`}
                        />
                    </button>
                    Only new products
                </label>

                <label className="inline-flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
                    <button
                        type="button"
                        role="switch"
                        aria-checked={filters.onlyToBeDestroyed === '1'}
                        // Switching on widens the class filter to all classes: about a third of the
                        // flagged articles sit in class 1-4 and would hide behind the Piombi default.
                        onClick={() => onFilterChange(filters.onlyToBeDestroyed === '1'
                            ? { ...filters, onlyToBeDestroyed: undefined }
                            : { ...filters, onlyToBeDestroyed: '1', inventoryClass: undefined, classGroup: 'all' })}
                        className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1 ${filters.onlyToBeDestroyed === '1' ? 'bg-red-600' : 'bg-gray-300'}`}
                    >
                        <span
                            className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition ${filters.onlyToBeDestroyed === '1' ? 'translate-x-4' : 'translate-x-0'}`}
                        />
                    </button>
                    Only to be destroyed
                </label>
            </div>
        </div>
    );
}
